import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMutation } from './mutation.js';
import { api, type AppelApi } from './client.js';
import { appelDeRequete, cleMetier, dansLaPortee, dansLaPorteeCourante } from './cles.js';

export type PlateformeDiffusion = 'artsy' | 'artnet';
export type EtatDiffusion = 'en_attente' | 'diffusee' | 'refusee' | 'retiree';
export interface FluxDiffusion {
  id: string;
  plateforme: PlateformeDiffusion;
  actif: boolean;
  toutesLesEligibles: boolean;
  oeuvresChoisies: readonly string[];
  langue: 'fr' | 'en';
  lien: boolean;
  lienCreeLe: string | null;
  revision: number;
  dernierDepot: {
    reference: string;
    empreinte: string;
    deposeLe: string;
    nombre: number;
    statut: string;
    rejets: readonly { reference: string; motif: string }[];
  } | null;
  derniereErreur: { le: string; code: string } | null;
}
export interface EtatOeuvreDiffusion {
  plateforme: PlateformeDiffusion;
  oeuvreId: string;
  reference: string;
  etat: EtatDiffusion;
  erreurs: readonly { code: string; champ: string }[];
  diffuseeLe: string | null;
  retireeLe: string | null;
  majLe: string;
}
export interface ApercuDiffusion {
  oeuvres: readonly {
    reference: string;
    titre: string;
    artiste: string;
    technique: string | null;
    dimensions: string | null;
    prix: { montantCentimes: number; devise: string } | null;
    prixSurDemande: boolean;
  }[];
  refusees: readonly {
    oeuvreId: string;
    titre: string;
    motifs: readonly { code: string; champ: string }[];
  }[];
}
export interface CorpsFlux {
  revision: number;
  actif?: boolean;
  toutesLesEligibles?: boolean;
  langue?: 'fr' | 'en';
}
const chemin = (g: string, suite: string) => `/g/${encodeURIComponent(g)}/diffusion${suite}`;
const options = (galerie: string, appel?: AppelApi) => ({ galerie, ...appel });
export const diffusionApi = {
  flux: (g: string, appel?: AppelApi) =>
    api.get<{ flux: readonly FluxDiffusion[]; eligibles: number }>(
      chemin(g, '/flux'),
      options(g, appel),
    ),
  unFlux: (g: string, p: PlateformeDiffusion, appel?: AppelApi) =>
    api.get<{ flux: FluxDiffusion; compteurs: Record<EtatDiffusion, number> }>(
      chemin(g, `/flux/${p}`),
      options(g, appel),
    ),
  creerFlux: (g: string, p: PlateformeDiffusion, appel?: AppelApi) =>
    api.post<{ flux: FluxDiffusion }>(chemin(g, '/flux'), { plateforme: p }, options(g, appel)),
  modifierFlux: (g: string, p: PlateformeDiffusion, corps: CorpsFlux, appel?: AppelApi) =>
    api.patch<{ flux: FluxDiffusion }>(chemin(g, `/flux/${p}`), corps, options(g, appel)),
  choisir: (g: string, p: PlateformeDiffusion, id: string, choisie: boolean, appel?: AppelApi) =>
    api.put<{ flux: FluxDiffusion }>(
      chemin(g, `/flux/${p}/oeuvres/${encodeURIComponent(id)}`),
      { choisie },
      options(g, appel),
    ),
  apercu: (g: string, p: PlateformeDiffusion, appel?: AppelApi) =>
    api.get<ApercuDiffusion>(chemin(g, `/flux/${p}/apercu`), options(g, appel)),
  synchroniser: (g: string, p: PlateformeDiffusion, appel?: AppelApi) =>
    api.post<{ demande: boolean }>(chemin(g, `/flux/${p}/synchroniser`), {}, options(g, appel)),
  creerLien: (g: string, p: PlateformeDiffusion, appel?: AppelApi) =>
    api.post<{ lien: { url: string; creeLe: string } }>(
      chemin(g, `/flux/${p}/lien`),
      {},
      options(g, appel),
    ),
  rapport: (g: string, p: PlateformeDiffusion, appel?: AppelApi) =>
    api.get<{ oeuvres: readonly EtatOeuvreDiffusion[] }>(
      chemin(g, `/oeuvres?plateforme=${p}`),
      options(g, appel),
    ),
  etatsOeuvre: (g: string, id: string, appel?: AppelApi) =>
    api.get<{ etats: readonly EtatOeuvreDiffusion[]; choisiePar: readonly PlateformeDiffusion[] }>(
      chemin(g, `/oeuvres/${encodeURIComponent(id)}`),
      options(g, appel),
    ),
};
export function useFluxDiffusion(g: string) {
  return useQuery({
    queryKey: cleMetier(g, 'diffusion', 'flux'),
    queryFn: (c) => diffusionApi.flux(g, appelDeRequete(c)),
  });
}
export function useUnFlux(g: string, p: PlateformeDiffusion) {
  return useQuery({
    queryKey: cleMetier(g, 'diffusion', 'flux', p),
    queryFn: (c) => diffusionApi.unFlux(g, p, appelDeRequete(c)),
  });
}
export function useApercuFlux(g: string, p: PlateformeDiffusion) {
  return useQuery({
    queryKey: cleMetier(g, 'diffusion', 'apercu', p),
    queryFn: (c) => diffusionApi.apercu(g, p, appelDeRequete(c)),
  });
}
export function useRapportDiffusion(g: string, p: PlateformeDiffusion) {
  return useQuery({
    queryKey: cleMetier(g, 'diffusion', 'rapport', p),
    queryFn: (c) => diffusionApi.rapport(g, p, appelDeRequete(c)),
  });
}
export function useEtatsDiffusionOeuvre(g: string, id: string) {
  return useQuery({
    queryKey: cleMetier(g, 'diffusion', 'oeuvre', id),
    queryFn: (c) => diffusionApi.etatsOeuvre(g, id, appelDeRequete(c)),
  });
}
function useGeste<T>(g: string, executer: (v: T, a: AppelApi) => Promise<unknown>) {
  const client = useQueryClient();
  return useMutation(
    dansLaPortee({
      gcTime: 0,
      mutationFn: executer,
      onSuccess: () => {
        void client.invalidateQueries({ queryKey: cleMetier(g, 'diffusion') });
      },
    }),
  );
}
export function useCreerFlux(g: string) {
  return useGeste(g, (p: PlateformeDiffusion, a) => diffusionApi.creerFlux(g, p, a));
}
export function useModifierFlux(g: string, p: PlateformeDiffusion) {
  return useGeste(g, (c: CorpsFlux, a) => diffusionApi.modifierFlux(g, p, c, a));
}
export function useChoisirOeuvre(g: string, id: string) {
  return useGeste(g, (v: { plateforme: PlateformeDiffusion; choisie: boolean }, a) =>
    diffusionApi.choisir(g, v.plateforme, id, v.choisie, a),
  );
}
export function useSynchroniserFlux(g: string, p: PlateformeDiffusion) {
  return useGeste(g, (_v: void, a) => diffusionApi.synchroniser(g, p, a));
}
/** Le lien passe directement dans l'état local du dialogue, jamais dans un cache TanStack. */
export function useCreerLienFlux(g: string, p: PlateformeDiffusion) {
  return () => dansLaPorteeCourante((a) => diffusionApi.creerLien(g, p, a));
}
